// src/store/mypage/mypageListsSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type Id = string;

export type RemoveInterestPayload = { feedId: string; sector: string };

export const makeRemoveInterestKey = (p: RemoveInterestPayload): string =>
    `${String(p.sector).trim()}::${String(p.feedId).trim()}`;

type DeletingMap = Record<string, boolean>;

export type MyPageListsState = {
  // 저장한 이벤트 삭제 in-flight
  deletingSaved: DeletingMap;

  // 알림 삭제 in-flight
  deletingAlerts: DeletingMap;

  // 관심 테마(섹터/티커) 제거 in-flight (현재는 saved 삭제에도 사용 중)
  removingInterest: DeletingMap;

  lastError?: string | null;
};

const initialState: MyPageListsState = {
  deletingSaved: {},
  deletingAlerts: {},
  removingInterest: {},
  lastError: null,
};

const slice = createSlice({
  name: 'mypageLists',
  initialState,
  reducers: {
    /* ---------------------------
     * 저장 이벤트 삭제 관련 (기존 유지: 단일 id)
     * --------------------------- */
    deleteSavedRequested(state, action: PayloadAction<Id>) {
      state.deletingSaved[action.payload] = true;
      state.lastError = null;
    },
    deleteSavedSucceeded(state, action: PayloadAction<Id>) {
      delete state.deletingSaved[action.payload];
    },
    deleteSavedFailed(state, action: PayloadAction<{ id: Id; error: string }>) {
      delete state.deletingSaved[action.payload.id];
      state.lastError = action.payload.error;
    },

    /* ---------------------------
     * 알림 삭제 관련 (기존 유지)
     * --------------------------- */
    deleteAlertRequested(state, action: PayloadAction<Id>) {
      state.deletingAlerts[action.payload] = true;
      state.lastError = null;
    },
    deleteAlertSucceeded(state, action: PayloadAction<Id>) {
      delete state.deletingAlerts[action.payload];
    },
    deleteAlertFailed(state, action: PayloadAction<{ id: Id; error: string }>) {
      delete state.deletingAlerts[action.payload.id];
      state.lastError = action.payload.error;
    },

    /* ---------------------------
     * 관심 테마 제거 관련
     * (sector, feedId) 복합키로 변경
     * --------------------------- */
    removeInterestRequested(state, action: PayloadAction<RemoveInterestPayload>) {
      const k = makeRemoveInterestKey(action.payload);
      state.removingInterest[k] = true;
      state.lastError = null;
    },
    removeInterestSucceeded(state, action: PayloadAction<RemoveInterestPayload>) {
      const k = makeRemoveInterestKey(action.payload);
      delete state.removingInterest[k];
    },
    removeInterestFailed(
        state,
        action: PayloadAction<{ payload: RemoveInterestPayload; error: string }>
    ) {
      const k = makeRemoveInterestKey(action.payload.payload);
      delete state.removingInterest[k];
      state.lastError = action.payload.error;
    },
  },
});

export const {
  deleteSavedRequested,
  deleteSavedSucceeded,
  deleteSavedFailed,

  deleteAlertRequested,
  deleteAlertSucceeded,
  deleteAlertFailed,

  removeInterestRequested,
  removeInterestSucceeded,
  removeInterestFailed,
} = slice.actions;

export default slice.reducer;
